// ZIP -> county lookup for the PPE search form.
//
// data/zip<N>.json is generated by build_data.py from the Census 2020
// ZCTA-to-county relationship file, with county names spelled exactly as
// /data/loanlimits (and so the County select) spells them. Each ZIP lists its
// counties by share of the ZIP's land, so the first is the one to preselect.
// A chunk holds every ZIP starting with the same digit and is only fetched the
// first time a ZIP starting with that digit is looked up.

const chunks = {};

const loadChunk = (digit) => {
  if (!chunks[digit]) {
    chunks[digit] = import(`./data/zip${digit}.json`)
      .then((module) => module.default || module)
      .catch((err) => {
        delete chunks[digit];
        throw err;
      });
  }
  return chunks[digit];
};

// The 5-digit ZIP of a "12345" or "12345-6789" value, else null.
export const zip5 = (value) => {
  const match = /^(\d{5})(?:-\d{4})?$/.exec(String(value ?? "").trim());
  return match ? match[1] : null;
};

// Resolves to the ZIP's counties, main county first, as [{ state, county }];
// [] when the ZIP is not in the Census list (PO box and single-business ZIPs
// are not); null when the value is not a ZIP.
export const lookupZipCounties = (value) => {
  const zip = zip5(value);
  if (!zip) return Promise.resolve(null);
  return loadChunk(zip[0]).then((chunk) => (chunk.zips[zip] || []).map((i) => ({
    state: chunk.counties[i][0],
    county: chunk.counties[i][1],
  })));
};

// True unless the ZIP is known and the state/county is not one of its counties.
export const zipMatchesLocation = (counties, state, county) => (
  !Array.isArray(counties)
  || counties.length === 0
  || counties.some((c) => c.state === state && c.county === county)
);

const offeredBy = (loanLimits) => (c) => (
  !loanLimits || !loanLimits[c.state] || c.county in loanLimits[c.state]
);

// The state/county the form should show after the ZIP is entered: the current
// one when the ZIP lies in it, otherwise the ZIP's main county (skipping any
// the County select does not offer). null when the ZIP gives nothing to fill.
export const locationForZip = (counties, current = {}, loanLimits) => {
  if (!Array.isArray(counties) || counties.length === 0) return null;
  if (zipMatchesLocation(counties, current.state, current.county)) {
    return { state: current.state, county: current.county };
  }
  const pick = counties.find(offeredBy(loanLimits)) || counties[0];
  return { state: pick.state, county: pick.county };
};

// The ZIP's counties in one state, main first, limited to those the County
// select offers.
export const zipCountiesInState = (counties, state, loanLimits) => (
  Array.isArray(counties)
    ? counties.filter((c) => c.state === state).filter(offeredBy(loanLimits)).map((c) => c.county)
    : []
);

const titleCase = (value) => String(value || "")
  .toLowerCase()
  .replace(/(^|[\s-])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());

// "ALAMEDA COUNTY", "CALIFORNIA" -> "Alameda County, California"
export const describeLocation = (state, county) => (
  [titleCase(county), titleCase(state)].filter(Boolean).join(", ")
);
